import { useState, useMemo, useEffect } from 'react';
import { ActivityItem, ActivityFilterCriteria, FilterOption } from '../types';

interface UseActivityFilteringProps {
  activities: ActivityItem[];
  isServerSide?: boolean;
}

interface UseActivityFilteringReturn {
  filteredActivities: ActivityItem[];
  reportTypeFilterOptions: FilterOption[];
  reportNameFilterOptions: FilterOption[];
  statusFilterOptions: FilterOption[];
  selectedReportType: string;
  selectedReportName: string;
  selectedStatus: string;
  setSelectedReportType: (value: string) => void;
  setSelectedReportName: (value: string) => void;
  setSelectedStatus: (value: string) => void;
  isLoading: boolean;
}

export const useActivityFiltering = ({
  activities,
  isServerSide = false,
}: UseActivityFilteringProps): UseActivityFilteringReturn => {
  const [selectedReportType, setSelectedReportType] = useState<string>('all');
  const [selectedReportName, setSelectedReportName] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  // Simulate loading on initial mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); // 1.5 seconds loading simulation

    return () => clearTimeout(timer);
  }, []);

  // Generate filter options dynamically from activities data
  const reportTypeFilterOptions = useMemo(() => {
    const uniqueTypes = Array.from(new Set(activities.map(activity => activity.category)));
    return [
      { label: 'All Types', value: 'all' },
      ...uniqueTypes.map(type => ({
        label: `${type} - ${getTypeDescription(type)}`,
        value: type
      }))
    ];
  }, [activities]);

  const reportNameFilterOptions = useMemo(() => {
    const filteredByType = selectedReportType === 'all' 
      ? activities 
      : activities.filter(activity => activity.category === selectedReportType);
    
    const uniqueNames = Array.from(new Set(filteredByType.map(activity => activity.reportName)));
    return [
      { label: 'All Reports/Analyses', value: 'all' },
      ...uniqueNames.map(name => ({
        label: name,
        value: name
      }))
    ];
  }, [activities, selectedReportType]);

  const statusFilterOptions = useMemo(() => {
    const uniqueStatuses = Array.from(new Set(activities.map(activity => activity.status)));
    return [
      { label: 'All Statuses', value: 'all' },
      ...uniqueStatuses.map(status => ({
        label: status,
        value: status
      }))
    ];
  }, [activities]);

  // Filter activities based on selected criteria
  const filteredActivities = useMemo(() => {
    if (isServerSide) {
      // For server-side filtering, return activities as-is (server handles filtering)
      return activities;
    }

    return activities.filter(activity => {
      const matchesType = selectedReportType === 'all' || activity.category === selectedReportType;
      const matchesName = selectedReportName === 'all' || activity.reportName === selectedReportName;
      const matchesStatus = selectedStatus === 'all' || activity.status === selectedStatus;

      return matchesType && matchesName && matchesStatus;
    });
  }, [activities, selectedReportType, selectedReportName, selectedStatus, isServerSide]);

  return {
    filteredActivities,
    reportTypeFilterOptions,
    reportNameFilterOptions,
    statusFilterOptions,
    selectedReportType,
    selectedReportName,
    selectedStatus,
    setSelectedReportType,
    setSelectedReportName,
    setSelectedStatus,
    isLoading,
  };
};

// Helper function to get type descriptions
const getTypeDescription = (type: string): string => {
  switch (type) {
    case 'RVM':
      return 'Rate Volume Mix';
    case 'NetPPM':
      return 'Net Price Per Unit';
    case 'MFR':
      return 'Monthly Financial Report';
    case 'OPEX':
      return 'Operational Expenditure';
    default:
      return type;
  }
};
